import { LLMCustomer, LLMUsageTier } from "../API";

/**
 * Gets the effective replenishment amounts for a customer.
 * If customer has a usageTier, use those values.
 * If usageTier is null/undefined, all replenishment amounts are 0.
 * NO inheritance logic - simple and direct.
 */
export const getCustomerReplenishmentAmounts = (customer: LLMCustomer) => {
  const usageTier = customer.usageTier;
  
  if (!usageTier) {
    // No usage tier = no replenishment
    return {
      standardTokenReplenishmentAmount: 0,
      premiumTokenReplenishmentAmount: 0,
      superPremiumTokenReplenishmentAmount: 0,
      reasoningTokenReplenishmentAmount: 0,
      standardCallReplenishmentAmount: 0,
      premiumCallReplenishmentAmount: 0,
      superPremiumCallReplenishmentAmount: 0,
      reasoningCallReplenishmentAmount: 0,
    };
  }

  // Use values from usage tier, defaulting to 0 if null/undefined
  return {
    standardTokenReplenishmentAmount: usageTier.standardTokenReplenishmentAmount || 0,
    premiumTokenReplenishmentAmount: usageTier.premiumTokenReplenishmentAmount || 0,
    superPremiumTokenReplenishmentAmount: usageTier.superPremiumTokenReplenishmentAmount || 0,
    reasoningTokenReplenishmentAmount: usageTier.reasoningTokenReplenishmentAmount || 0,  
    standardCallReplenishmentAmount: usageTier.standardCallReplenishmentAmount || 0,
    premiumCallReplenishmentAmount: usageTier.premiumCallReplenishmentAmount || 0,
    superPremiumCallReplenishmentAmount: usageTier.superPremiumCallReplenishmentAmount || 0,
    reasoningCallReplenishmentAmount: usageTier.reasoningCallReplenishmentAmount || 0,
  };
};

/**
 * Gets the replenishment period for a customer.
 * If customer has a usageTier, use that period.
 * If usageTier is null/undefined, returns null (no replenishment scheduled).
 */
export const getCustomerReplenishmentPeriod = (customer: LLMCustomer) => {
  const usageTier = customer.usageTier;
  return usageTier?.replenishmentPeriod || null;
};

/**
 * Gets the replenishment method for a customer.
 * If customer has a usageTier, use that method.
 * If usageTier is null/undefined, returns null.
 */
export const getCustomerReplenishmentMethod = (customer: LLMCustomer) => {
  const usageTier = customer.usageTier;
  return usageTier?.replenishmentMethod || null;
};

/**
 * Gets the usage limit action for a customer.
 * If customer has a usageTier, use that action.
 * If usageTier is null/undefined, returns null.
 */
export const getCustomerUsageLimitAction = (customer: LLMCustomer) => {
  const usageTier = customer.usageTier;
  return usageTier?.usageLimitAction || null;
};

/**
 * Gets the usage tier name for display purposes.
 * Returns "No Usage Tier" if customer has no usage tier.
 */
export const getCustomerUsageTierName = (customer: LLMCustomer) => {
  const usageTier = customer.usageTier;
  return usageTier?.name || "No Usage Tier";
};

/**
 * Calculates the next replenishment date for a customer.
 * Returns null if no replenishment is scheduled.
 */
export const getNextReplenishmentDate = (customer: LLMCustomer): Date | null => {
  const period = getCustomerReplenishmentPeriod(customer);
  
  if (!period || period === 'NONE' || !customer.lastReplenishedAt) {
    return null;
  }

  const lastReplenished = new Date(customer.lastReplenishedAt);
  const nextDate = new Date(lastReplenished);
  
  switch (period) {
    case "DAILY":
      nextDate.setDate(nextDate.getDate() + 1);
      break;
    case "WEEKLY":
      nextDate.setDate(nextDate.getDate() + 7);
      break;
    case "MONTHLY":
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
    case "YEARLY":
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
    default:
      return null;
  }
  
  return nextDate;
};
